import React, { useState } from 'react';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { ArrowsRightLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { parseXmlFianza, parseXmlFactura } from '../utils/xmlParsers';
import { useTarifas } from '../contexts/DataContext';
import CompactFileReceiver from './CompactFileReceiver';
import {
  ESTADO_FIANZA,
  EVENTO_TIPOS,
  createEmisionEvento,
  createEmisionFactura,
  buildEvento,
  applyEndoso,
} from '../utils/fianzaEventos';
import {
  calculateTarifa,
  calculateComision,
  getPrimaAmount,
  getEmisorNombre,
  getEmisorRfc,
  getSubRamo,
  getTipoFianza,
  toTimestamp,
  partitionTarifas,
} from '../utils/calculations';
import { recordComisionEvento } from '../api/comisiones';
import { fianzaStoragePath } from '../utils/contratoStorage';

// A small file-drop row (same shape as EndosoDialog's).
function FileSlot({ label, type, file, onFile, disabled }) {
  return (
    <div>
      <div className="text-xs font-medium text-azul-surelia mb-1">{label}</div>
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-cian-digital/40 bg-cian-digital/5 px-3 py-2">
          <span className="text-xs text-gray-700 truncate">{file.name}</span>
          <button type="button" onClick={() => onFile(null)} disabled={disabled} className="text-gray-400 hover:text-red-500 shrink-0">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <CompactFileReceiver
          type={type}
          disabled={disabled}
          onFilesChange={(files) => onFile(files?.[0] || null)}
          placeholder={type === 'xml' ? 'Arrastra o elige XML' : 'Arrastra o elige PDF'}
        />
      )}
    </div>
  );
}

/**
 * SustituirFianzaDialog — replaces a fianza with a NEW fianza entity.
 *
 * The old fianza is marked `sustituida` (+ `sustituidaPor`), the new one is
 * created active (+ `sustituyeA`). Two money modes:
 *   - Monto/factura cambia (default): the old comisión is reversed and the new
 *     fianza books its own emisión — comisión re-attributes to the new entity.
 *   - Misma factura (solo cambia el documento): no money moves — the old
 *     comisión continues; no reversal, no new emisión movement.
 * Both fianzas + the reversal are written atomically through the backend.
 */
export function SustituirFianzaDialog({ isOpen, onClose, fianza, contratoData, clientData, convenio, user, userData, onApplied, buildBitacoraMeta }) {
  const { tarifas } = useTarifas();
  const createdBy = { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null };

  const [fianzaXmlFile, setFianzaXmlFile] = useState(null);
  const [facturaXmlFile, setFacturaXmlFile] = useState(null);
  const [fianzaPdfFile, setFianzaPdfFile] = useState(null);
  const [facturaPdfFile, setFacturaPdfFile] = useState(null);
  const [parsedFianza, setParsedFianza] = useState(null);
  const [parsedFactura, setParsedFactura] = useState(null);
  const [montoCambia, setMontoCambia] = useState(true);
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setFianzaXmlFile(null); setFacturaXmlFile(null);
    setFianzaPdfFile(null); setFacturaPdfFile(null);
    setParsedFianza(null); setParsedFactura(null);
    setMontoCambia(true); setNota(''); setError(null);
    onClose();
  };

  const onFianzaXml = async (file) => {
    if (!file) { setFianzaXmlFile(null); setParsedFianza(null); return; }
    setError(null);
    const res = await parseXmlFianza(file);
    if (!res.success) { setError(`XML de fianza inválido: ${res.error}`); return; }
    setParsedFianza(res.data); setFianzaXmlFile(file);
  };
  const onFacturaXml = async (file) => {
    if (!file) { setFacturaXmlFile(null); setParsedFactura(null); return; }
    setError(null);
    const res = await parseXmlFactura(file);
    if (!res.success) { setError(`XML de factura inválido: ${res.error}`); return; }
    setParsedFactura(res.data); setFacturaXmlFile(file);
  };

  const handleSubmit = async () => {
    if (!parsedFianza && !parsedFactura) {
      setError('Sube al menos el XML de la nueva fianza o factura.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ts = Date.now();
      const newFianzaId = `fianza_${ts}`;
      const basePath = fianzaStoragePath(contratoData, clientData?.id, newFianzaId);

      // Upload the new fianza's files.
      const filePaths = {};
      const uploads = [];
      const pushUpload = (file, name, key) => {
        if (!file) return;
        const p = `${basePath}/${name}`;
        uploads.push(uploadBytes(ref(storage, p), file));
        filePaths[key] = p;
      };
      pushUpload(fianzaXmlFile, 'fianza.xml', 'fianzaXml');
      pushUpload(facturaXmlFile, 'factura.xml', 'facturaXml');
      pushUpload(fianzaPdfFile, 'fianza.pdf', 'fianzaPdf');
      pushUpload(facturaPdfFile, 'factura.pdf', 'facturaPdf');
      await Promise.all(uploads);

      // Same-factura sustitución (only the fianza document changed): the new
      // fianza reuses the previous factura's files instead of re-uploading them.
      if (!montoCambia) {
        if (fianza?.filePaths?.facturaXml) filePaths.facturaXml = fianza.filePaths.facturaXml;
        if (fianza?.filePaths?.facturaPdf) filePaths.facturaPdf = fianza.filePaths.facturaPdf;
      }

      // Build the new fianza entity (same shape as UploadFianza) + sustituyeA.
      const nuevaFianza = {
        id: newFianzaId,
        contratoId: contratoData.id,
        createdAt: ts,
        filePaths,
        xmlFianza: parsedFianza || null,
        // Same-factura sustitución inherits the previous factura's parsed data.
        xmlFactura: parsedFactura || (!montoCambia ? (fianza?.xmlFactura || null) : null),
        numeroFianza: parsedFianza?.numeroFianza || parsedFactura?.noIdentificacion || null,
        montoAfianzado: parsedFianza?.montoAfianzado || null,
        monto: parsedFactura?.total ?? fianza?.monto ?? null,
        tipoFianza: parsedFianza?.tipo || null,
        inicioVigenciaFianza: parsedFianza?.inicioVigenciaFianza || null,
        finVigenciaFianza: parsedFianza?.finVigenciaFianza || null,
        pagada: false,
        renovable: Boolean(fianza?.renovable),
        // The convenio-split context survives the sustitución.
        beneficiarioConvenio: Boolean(fianza?.beneficiarioConvenio),
        estadoFianza: ESTADO_FIANZA.ACTIVA,
        sustituyeA: fianza.id,
      };
      // Lock the comisión % for the new entity. Convenio fianzas inherit the
      // old fianza's captured % (the fiado agent's deal — the org's tarifa
      // tables never apply); when unknown, null lets the backend estimate from
      // the convenio-scope tables or book pct-pending.
      if (contratoData?.convenioId) {
        nuevaFianza.comisionPct = fianza?.comisionPct ?? null;
        // Same convenio, same other agent, same deal: a % a person confirmed
        // carries its confirmation into the substitute.
        if (fianza?.comisionPctConfirmada && nuevaFianza.comisionPct != null) {
          nuevaFianza.comisionPctConfirmada = fianza.comisionPctConfirmada;
        }
      } else {
        const prima = getPrimaAmount(nuevaFianza);
        const montoAf = Number(nuevaFianza.montoAfianzado) || 0;
        const tarifa = calculateTarifa(prima, montoAf, {
          truncate: true,
          vigencia: {
            inicio: toTimestamp(nuevaFianza.inicioVigenciaFianza),
            fin: toTimestamp(nuevaFianza.finVigenciaFianza),
          },
        });
        const { comisionPct, pctEstimado } = calculateComision(
          prima, tarifa, partitionTarifas(tarifas).propias,
          getEmisorNombre(nuevaFianza), getSubRamo(nuevaFianza), getTipoFianza(nuevaFianza),
          getEmisorRfc(nuevaFianza)
        );
        // A Tabla-general % is an estimate — leave it unlocked so the backend
        // books it pctEstimado ("por confirmar").
        nuevaFianza.comisionPct = pctEstimado ? null : comisionPct ?? null;
      }
      nuevaFianza.eventos = [createEmisionEvento(nuevaFianza)];
      nuevaFianza.facturas = [createEmisionFactura(nuevaFianza)];

      // Terminal sustitución event on the OLD fianza.
      const evento = buildEvento({
        tipo: EVENTO_TIPOS.SUSTITUCION,
        origen: 'fianza',
        createdBy,
        nota: nota.trim() || null,
        sustituidaPorFianzaId: newFianzaId,
        ts,
      });
      const nextOld = applyEndoso(fianza, evento, { sustituidaPor: newFianzaId });

      // 1) Write both fianzas atomically; reverse the old comisión only when the
      //    monto/factura changes (pass a factura marker to trigger the reversal).
      await recordComisionEvento({
        contractId: contratoData.id,
        fianzaId: fianza.id,
        eventoTipo: 'sustitucion',
        eventoId: evento.id,
        nextFianza: nextOld,
        nuevaFianza,
        factura: montoCambia ? nuevaFianza.facturas[0] : null,
        fecha: ts,
      });

      // The new fianza's emisión movement is booked by that same call, inside the
      // same transaction. It used to be a second round-trip, which meant a
      // failure between the two left the old comisión reversed and the new
      // fianza earning nothing — with nothing in the data to say so.

      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoData.id,
          user,
          userData,
          metadata: buildBitacoraMeta
            ? buildBitacoraMeta(`Sustituyó fianza #${fianza.numeroFianza || 'sin número'}`, { fianzaId: fianza.id, sustituidaPor: newFianzaId })
            : { contratoId: contratoData.id, fianzaId: fianza.id, description: `Sustituyó fianza #${fianza.numeroFianza || 'sin número'}`, sustituidaPor: newFianzaId },
        });
      } catch (e) { console.error('bitacora sustitucion failed', e); }

      onApplied?.();
      resetAndClose();
    } catch (e) {
      console.error('sustitucion failed', e);
      setError(e.message || 'No se pudo sustituir la fianza.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={submitting ? () => {} : resetAndClose} size="2xl">
      <DialogTitle><span className="text-azul-surelia">Sustituir fianza</span></DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            La fianza actual quedará <span className="font-semibold">sustituida</span> y se creará una nueva fianza que la reemplaza. Sube los documentos de la nueva fianza.
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={montoCambia}
              onChange={(e) => {
                const checked = e.target.checked;
                setMontoCambia(checked);
                if (!checked) { setFacturaXmlFile(null); setFacturaPdfFile(null); setParsedFactura(null); }
              }}
              disabled={submitting}
              className="mt-0.5"
            />
            <span className="text-xs text-gray-700">
              <span className="font-medium">¿Cambió el monto o la factura?</span> Márcalo si esta sustitución modifica el monto afianzado o corresponde a otra factura. La fianza anterior se anula y su comisión se revierte; la nueva fianza genera su propia comisión. Déjalo sin marcar si solo estás reemplazando la fianza, no la factura (mismo monto): la comisión anterior no cambia.
            </span>
          </label>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowsRightLeftIcon className="w-4 h-4 text-azul-surelia" />
              <h4 className="text-sm font-semibold text-gray-800">Documentos de la nueva fianza</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FileSlot label="XML fianza" type="xml" file={fianzaXmlFile} onFile={onFianzaXml} disabled={submitting} />
              <FileSlot label="PDF fianza" type="pdf" file={fianzaPdfFile} onFile={setFianzaPdfFile} disabled={submitting} />
              {montoCambia && (
                <>
                  <FileSlot label="XML factura" type="xml" file={facturaXmlFile} onFile={onFacturaXml} disabled={submitting} />
                  <FileSlot label="PDF factura" type="pdf" file={facturaPdfFile} onFile={setFacturaPdfFile} disabled={submitting} />
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota (opcional)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
              placeholder="Motivo de la sustitución…" />
          </div>

          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}


          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
            <Button color="surelia" onClick={handleSubmit} disabled={submitting || (!parsedFianza && !parsedFactura)}>
              {submitting ? 'Sustituyendo…' : 'Sustituir fianza'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default SustituirFianzaDialog;
